import { python } from "@codemirror/lang-python";
import { sql } from "@codemirror/lang-sql";
import { githubLight } from "@uiw/codemirror-theme-github";
import CodeMirror, {
	EditorView,
	type ViewUpdate,
} from "@uiw/react-codemirror";
import {
	useCallback,
	useMemo,
	useRef,
	type FC,
} from "react";

import {
	getRenderedEditorValue,
	getStoredEditorValue,
} from "./editorPadding";
import { useCodeMirrorPlaceholderPosition } from "./useCodeMirrorPlaceholderPosition";
import { useMinimumEditorLineCount } from "./useMinimumEditorLineCount";
import { formulaCodeTheme } from "./formulaCodeTheme";

type TCodeEditorProps = {
	value: string;
	setValue?: (val: string) => void;
	readOnly?: boolean;
	className?: string;
	showLineNumber?: boolean;
	highlightActiveLine?: boolean;
	showFoldGutter?: boolean;
	minHeight?: string;
	maxHeight?: string;
	height?: string;
	placeholder?: string;
	language?: "python" | "sql";
	codeTheme?: "default" | "formula";
	editorPaddingLines?: number;
};

export const CodeEditor: FC<TCodeEditorProps> = ({
	value,
	setValue,
	readOnly = true,
	className,
	showLineNumber = false,
	highlightActiveLine = false,
	showFoldGutter = false,
	minHeight,
	maxHeight,
	height,
	placeholder,
	language = "python",
	codeTheme = "default",
	editorPaddingLines = 0,
}) => {
	const onChange = useCallback(
		(val: string, viewUpdate: ViewUpdate) => {
			if (setValue) {
				const changedRanges: Array<{
					fromA: number;
					toA: number;
					fromB: number;
					toB: number;
				}> = [];
				viewUpdate.changes.iterChangedRanges(
					(fromA, toA, fromB, toB) => {
						changedRanges.push({
							fromA,
							toA,
							fromB,
							toB,
						});
					},
					true
				);
				setValue(
					getStoredEditorValue(
						val,
						value,
						viewUpdate.changes.mapPos(
							value.length,
							1
						),
						changedRanges
					)
				);
			}
		},
		[setValue, value]
	);

	const languageExtension = useMemo(() => {
		return language === "sql" ? sql() : python();
	}, [language]);

	const shellRef = useRef<HTMLDivElement | null>(null);
	const fallbackMinimumLineCount =
		editorPaddingLines > 0 ? editorPaddingLines + 1 : 1;
	const minimumLineCount = useMinimumEditorLineCount({
		shellRef,
		enabled: editorPaddingLines > 0,
		fallbackMinimumLineCount,
		height,
		minHeight,
	});
	const shouldShowPlaceholder =
		Boolean(placeholder) &&
		value.length === 0 &&
		minimumLineCount > 1;
	const codeMirrorPlaceholder =
		editorPaddingLines > 0 ? "" : placeholder ?? "";
	const placeholderStyle = useCodeMirrorPlaceholderPosition(
		shellRef,
		shouldShowPlaceholder
	);

	return (
		<div
			className="code-mirror-shell"
			ref={shellRef}
		>
			<CodeMirror
				className={`code-mirror-component ${className ?? ""}`}
				value={getRenderedEditorValue(
					value,
					minimumLineCount
				)}
				extensions={[languageExtension, EditorView.lineWrapping]}
				onChange={onChange}
				readOnly={readOnly}
				theme={
					codeTheme === "formula"
						? formulaCodeTheme
						: githubLight
				}
				basicSetup={{
					lineNumbers: showLineNumber,
					highlightActiveLine,
					highlightActiveLineGutter: highlightActiveLine,
					foldGutter: showFoldGutter,
				}}
				minHeight={minHeight ?? "auto"}
				maxHeight={maxHeight ?? "auto"}
				height={height ?? "auto"}
				placeholder={codeMirrorPlaceholder}
			/>
			{shouldShowPlaceholder && (
				<div
					className="code-mirror-placeholder"
					style={placeholderStyle}
				>
					{placeholder}
				</div>
			)}
		</div>
	);
};
